import React, { useContext, useEffect } from 'react';
import { SIDE_DRAWER_ANIMATION_DURATION, SideDrawerContainerContext, SideDrawerContent } from './SideDrawerContainer';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import Stack, { contentStackSpacing } from '../Stack';
import Text from '../Text';
import IconButton from '../IconButton';
import { View } from 'react-native';
import { colors } from '../../../utils/theme';
import Divider from '../Divider';
import DrawerContentContainer from './DrawerContentContainer';

export type MaybeRenderedSideDrawerOwnProps = {
    mobileOnFocus: () => void;
    isOpen: boolean;
    children: (onClose: (force: boolean) => void) => React.ReactNode;
    onClose: (force: boolean) => void;
    title: string;
    contentKey: string;
};

export type MaybeRenderedSideDrawerProps = MaybeRenderedSideDrawerOwnProps;

export type SideDrawerContentKeyContextValue = {
    contentKey: string;
};

export const SideDrawerContentKeyContext = React.createContext<SideDrawerContentKeyContextValue>({
    contentKey: '',
});

const MaybeRenderedSideDrawer: React.FC<MaybeRenderedSideDrawerProps> = ({
    mobileOnFocus,
    isOpen,
    children,
    title,
    onClose,
    contentKey,
}) => {
    const { openDrawer, closeDrawer, getShouldMountContent, forceCloseDrawer } = useContext(SideDrawerContainerContext);
    const isMobile = useIsMobileLayout();

    const shouldMountContent = getShouldMountContent(contentKey);
    React.useEffect(() => {
        if (isOpen) {
            if (isMobile) {
                mobileOnFocus();
            } else {
                openDrawer(contentKey);
            }
        } else {
            if (shouldMountContent) {
                forceCloseDrawer(contentKey);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    React.useEffect(() => {
        if (!shouldMountContent && isOpen) {
            onClose(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldMountContent]);

    return !isMobile && shouldMountContent ? (
        <SideDrawerContent>
            <Stack
                testID="drawer-content-container"
                vertical
                space={contentStackSpacing}
                fullWidth
                fullHeight
                style={{
                    backgroundColor: colors.White,
                }}
            >
                <DrawerContentContainer style={{ width: '100%' }}>
                    <Stack horizontal fullWidth justify={'space-between'} align="center">
                        <Text variant="h4">{title}</Text>
                        <IconButton icon="X" onPress={() => closeDrawer(contentKey)} testID="side-drawer-close" />
                    </Stack>
                </DrawerContentContainer>
                <Divider horizontal />
                <View style={{ flex: 1, width: '100%' }}>
                    <SideDrawerContentKeyContext.Provider value={{ contentKey }}>
                        {children((force) => (force ? forceCloseDrawer(contentKey) : closeDrawer(contentKey)))}
                    </SideDrawerContentKeyContext.Provider>
                </View>
            </Stack>
        </SideDrawerContent>
    ) : null;
};

export default React.memo(MaybeRenderedSideDrawer);

export type UseMaybeRenderedSideDrawerToggleArgs = {
    mobileOnFocus: () => void;
    renderContent: (onClose: (force: boolean) => void) => React.ReactNode;
    title: string;
    contentKey: string;
    noScroll?: boolean;
    onClosed?: () => void;
};

export type UseMaybeRenderedSideDrawerToggleReturnValue = {
    SideDrawer: React.ReactNode;
    onOpen: () => void;
};

export const useMaybeRenderedSideDrawerToggle = ({
    mobileOnFocus,
    renderContent,
    title,
    contentKey,
    onClosed,
}: UseMaybeRenderedSideDrawerToggleArgs): UseMaybeRenderedSideDrawerToggleReturnValue => {
    const [isOpen, setIsOpen] = React.useState(false);
    useEffect(() => {
        if (!isOpen && onClosed) {
            onClosed();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    return {
        SideDrawer: (
            <MaybeRenderedSideDrawer
                mobileOnFocus={() => {
                    mobileOnFocus();
                    // Close the drawer after the mobileOnFocus is called so that onBack will have this closed
                    setIsOpen(false);
                }}
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={title}
                contentKey={contentKey}
            >
                {renderContent}
            </MaybeRenderedSideDrawer>
        ),
        onOpen: () => {
            setIsOpen(true);
        },
    };
};

export type OnChainedOpenDrawer = () => void;

/**
 * Hook to open a side drawer from the action of a first drawer
 */
export const useChainedSideDrawer = (onOpenSecondDrawer: () => void): OnChainedOpenDrawer => {
    return () => {
        setTimeout(() => {
            onOpenSecondDrawer();
        }, SIDE_DRAWER_ANIMATION_DURATION + 100);
    };
};
